import { applyPromoCode, fetchCheckoutData } from "@/services/checkout.queries";
import { useToastNotify } from "@/stores/toastStore";
import { useIsFocused } from "@react-navigation/native";
import * as Sentry from "@sentry/react-native";
import { useQuery } from "@tanstack/react-query";
import { useT } from "@transifex/react";
import { CheckoutFailReason, PromoFailReason } from "ipctg-inflow-types/InFlowEnums";
import type { ParkCheckoutPOSTRequest, ParkCheckoutPOSTSuccessResponse, ParkPromoPOSTErrorResponse } from "ipctg-inflow-types/InFlowParkAPI";
import { useState } from "react";
import { Platform } from "react-native";

const handleError = (error: any) => {
  const errorFromAPI = (error as ParkPromoPOSTErrorResponse).failReason;
  let errorDescription = "An unexpected error has occurred.";

  switch (errorFromAPI) {
    case PromoFailReason.InvalidPassword:
      errorDescription = "Incorrect password.";
      break;
    case PromoFailReason.InvalidCode:
      errorDescription = "The code you entered isn't valid.";
      break;
    case PromoFailReason.MaxUsageReached:
      errorDescription = "This code has reached its usage limit and is no longer valid.";
      break;
    case PromoFailReason.InvalidForRate:
      errorDescription = "This code cannot be applied to the selected rate. Please select a different rate or use another code.";
      break;
    case PromoFailReason.InvalidForLot:
      errorDescription = "This code is not valid for the lot you are parked in.";
      break;
    case PromoFailReason.InvalidRequestFormat:
      break;
    case PromoFailReason.PromoInactive:
      errorDescription = "The code you entered isn't valid.";
      break;
    case PromoFailReason.PerCodeUserRestriction:
      errorDescription = "This promo code cannot be applied due to eligibility or usage limits.";
      break;
  }

  Sentry.captureException(error, {
    extra: {
      localStorage: Platform.OS === "web" ? window.localStorage : null,
    },
  });
  return errorDescription;
};

const handleApplyPromoCodeLogic = async ({
  promoCode,
  setIsPromoCodeLoading,
  setIsPromoCodeInvalid,
  setIsPromoApplied,
  setTransactionDetails,
  notify,
}: {
  promoCode: string;
  setIsPromoCodeLoading: (loading: boolean) => void;
  setIsPromoCodeInvalid: (invalid: boolean) => void;
  setIsPromoApplied: (applied: boolean) => void;
  setTransactionDetails: (details: any) => void;
  notify: any;
}) => {
  try {
    setIsPromoCodeLoading(true);
    const result = await applyPromoCode({ promoCode });
    if ("transactionDetails" in result) {
      setIsPromoApplied(true);
      setTransactionDetails(result.transactionDetails);
    } else {
      setIsPromoCodeInvalid(true);
    }
  } catch (error) {
    notify({
      severity: "error",
      message: handleError(error),
    });

    setIsPromoCodeInvalid(true);
  } finally {
    setIsPromoCodeLoading(false);
  }
};

const useCheckoutData = ({ rateID, isPurchasingPass }: ParkCheckoutPOSTRequest) => {
  const isFocused = useIsFocused();
  const { data, isLoading, isError, error, refetch, isFetching } = useQuery({
    queryKey: ["checkout", rateID, isPurchasingPass],
    queryFn: () => fetchCheckoutData({ rateID, isPurchasingPass }),
    enabled: !!rateID && isFocused,
    retry: false,
    staleTime: 0,
    gcTime: 0,
  });

  return { data, isLoading, isError, error, refetch, isFetching };
};

const usePromoCodeState = () => {
  const [promoCode, setPromoCode] = useState<string>("");
  const [discount, setDiscount] = useState<number | null>(null);
  const [isPromoCodeInvalid, setIsPromoCodeInvalid] = useState<boolean>(false);
  const [isPromoApplied, setIsPromoApplied] = useState<boolean>(false);
  const [transactionDetails, setTransactionDetails] = useState<ParkCheckoutPOSTSuccessResponse["transactionDetails"] | null>(null);

  return {
    promoCode,
    setPromoCode,
    discount,
    setDiscount,
    isPromoCodeInvalid,
    isPromoApplied,
    transactionDetails,
    setIsPromoCodeInvalid,
    setIsPromoApplied,
    setTransactionDetails,
  };
};

export const useCheckout = ({ rateID, isPurchasingPass }: ParkCheckoutPOSTRequest) => {
  const { notify } = useToastNotify();
  const [errorNotified, setErrorNotified] = useState(false);
  const [isPromoCodeLoading, setIsPromoCodeLoading] = useState(false);
  const [loadingRemovePromoCode, setLoadingRemovePromoCode] = useState(false);
  const t = useT();

  const { data, isLoading, error, refetch, isFetching } = useCheckoutData({ rateID, isPurchasingPass });

  const {
    promoCode,
    setPromoCode,
    discount,
    setDiscount,
    isPromoCodeInvalid,
    isPromoApplied,
    transactionDetails,
    setIsPromoCodeInvalid,
    setIsPromoApplied,
    setTransactionDetails,
  } = usePromoCodeState();

  if (error && !errorNotified) {
    if ("failReason" in error) {
      switch (error.failReason) {
        case CheckoutFailReason.SessionEnforced:
        case CheckoutFailReason.SessionInViolation:
          notify({
            severity: "error",
            message: t("This plate is currently in violation status. Parking activation is no longer available."),
          });
          break;
        default:
          notify({
            severity: "error",
            message: t("An error occurred when fetching checkout data"),
          });
          break;
      }
    } else {
      notify({
        severity: "error",
        message: t("An error occurred when fetching checkout data"),
      });
    }
    console.error("Error fetching checkout data:", error);
    setErrorNotified(true);
  }

  if (!error && errorNotified) {
    setErrorNotified(false);
  }

  const handleApplyPromoCode = async () => {
    await handleApplyPromoCodeLogic({
      promoCode,
      setIsPromoCodeLoading,
      setIsPromoCodeInvalid,
      setIsPromoApplied,
      setTransactionDetails,
      notify,
    });
  };

  const handleRemovePromoCode = async () => {
    setLoadingRemovePromoCode(true);
    await refetch();
    setPromoCode("");
    setDiscount(null);
    setIsPromoApplied(false);
    setLoadingRemovePromoCode(false);
  };

  const handleFocusInput = () => {
    setIsPromoCodeInvalid(false);
  };

  const clearPromoCodeState = () => {
    setPromoCode("");
    setIsPromoCodeInvalid(false);
    setIsPromoApplied(false);
    setDiscount(null);
  };

  return {
    loadingRemovePromoCode,
    isPromoCodeLoading,
    isFetching,
    error,
    promoCode,
    setPromoCode,
    discount,
    isPromoCodeInvalid,
    isPromoApplied,
    handleApplyPromoCode,
    handleRemovePromoCode,
    handleFocusInput,
    transactionDetails: isPromoApplied ? transactionDetails : data?.transactionDetails,
    clearPromoCodeState,
    refetch,
  };
};
